import { useEffect, useMemo, useRef, useState } from "react"
import { Card, CardContent } from "./ui/card"
import { Footprints, Gauge, Loader2, Mountain, ChevronRight, ChevronDown, MapPinned, ChartArea } from "lucide-react"
import { DataService } from "../services/data.service"
import { WalkModel, WalkPointModel, WalkPointClass, SEGMENT_STYLE, segmentClass, isWalkingSession, countsAsWalk, walkingMetersOf, walkZone } from "../models/walk.model"
import { DateTime } from "luxon"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "./ui/chart"
import { smoothTrack } from "../lib/gpsFilter"
import { DatePicker } from "./DatePicker"

export interface WalksReportProps {
    // null = legacy access exists but no modern person_user_id (no consent
    // record on wellnuo-backend) — render soft state without a request.
    beneficiaryUserId: number | null
    timezone: string
    // Day offset from today (0 = today, -1 = yesterday). Cascade-down from
    // dropdowns above (Location → Sleep → Walks). Local mode (Day/Week/1Mo/
    // 6Mo) is independent of the cascade.
    dateOffset: number
    onDateChange: (offset: number) => void
}

type Mode = 'day' | 'week' | 'month' | '6mo'

function formatDistance(meters: number): string {
    if (!isFinite(meters) || meters <= 0) return '0 m'
    if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`
    return `${Math.round(meters)} m`
}

function formatDuration(ms: number): string {
    if (!isFinite(ms) || ms <= 0) return '0 min'
    const totalMin = Math.round(ms / 60000)
    if (totalMin < 60) return `${totalMin} min`
    const h = Math.floor(totalMin / 60)
    const m = totalMin % 60
    return m ? `${h}h ${m}m` : `${h}h`
}

function formatSpeed(metersPerSec: number): string {
    if (!isFinite(metersPerSec) || metersPerSec <= 0) return '—'
    return `${(metersPerSec * 3.6).toFixed(1)} km/h`
}

// Haversine in meters for short segments — good enough for walk-scale points.
function haversine(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
    const R = 6371000
    const toRad = (d: number) => d * Math.PI / 180
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const la1 = toRad(a.lat), la2 = toRad(b.lat)
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2
    return 2 * R * Math.asin(Math.sqrt(x))
}

function hpaToAltMeters(hpa: number): number {
    // International Standard Atmosphere relative-altitude formula:
    //   h = 44330 * (1 - (P/P0)^(1/5.255))   with P0 = 1013.25 hPa
    // This gives an absolute reference; the chart plots the value relative
    // to the first point to render an elevation *profile*, not an altitude.
    return 44330 * (1 - Math.pow(hpa / 1013.25, 1 / 5.255))
}

// Simple centred moving-average smoothing so GPS jitter doesn't dominate
// the speed and cadence series. Window in samples.
function smooth(values: (number | null)[], window: number): (number | null)[] {
    const out: (number | null)[] = new Array(values.length)
    for (let i = 0; i < values.length; i++) {
        let sum = 0, n = 0
        for (let j = Math.max(0, i - window); j <= Math.min(values.length - 1, i + window); j++) {
            const v = values[j]
            if (v != null && isFinite(v)) { sum += v; n++ }
        }
        out[i] = n ? sum / n : null
    }
    return out
}

interface WalkSeries {
    minutes: number[]                      // minutes since walk start, per point
    speedKmh: (number | null)[]            // smoothed speed
    cadenceSpm: (number | null)[]          // smoothed steps/min (null if no step data)
    altitudeM: (number | null)[]           // relative to first point (null if no alt/hpa)
    hasSteps: boolean
    hasAlt: boolean
    walkStepsTotal: number | null          // last.steps - first.steps when available
    avgSpeedKmh: number | null
    distanceMeters: number                 // Kalman-smoothed track length (m)
    // Smoothed lat/lng, aligned 1:1 with the ts-sorted points, for the map
    // polyline. Drawing the cleaned track (not the raw zig-zag) is the
    // visible half of the same fix that corrects the distance.
    smoothedLatLng: { lat: number; lng: number }[]
}

function computeWalkSeries(points: WalkPointModel[]): WalkSeries {
    if (points.length === 0) {
        return { minutes: [], speedKmh: [], cadenceSpm: [], altitudeM: [], hasSteps: false, hasAlt: false, walkStepsTotal: null, avgSpeedKmh: null, distanceMeters: 0, smoothedLatLng: [] }
    }
    const sorted = [...points].sort((a, b) => a.ts - b.ts)
    const t0 = sorted[0].ts
    const minutes = sorted.map(p => (p.ts - t0) / 60000)

    // Kalman-smooth the GPS track first; every geometric quantity below
    // (per-segment speed, distance, avg speed) is derived from the cleaned
    // positions so the noise that inflated raw haversine sums is gone. The
    // smoother weights each fix by its own accuracy and never drops points,
    // so a bad jump is attenuated, not deleted.
    const smoothed = smoothTrack(sorted.map(p => ({ lat: p.lat, lng: p.lng, acc: p.acc, ts: p.ts })))
    const sm = smoothed.points

    // Per-segment speed (m/s) over the smoothed track, attributed to the
    // later point. The first sample has no preceding segment — null.
    const rawSpeedMs: (number | null)[] = sorted.map((p, i) => {
        if (i === 0) return null
        const dt = (p.ts - sorted[i - 1].ts) / 1000
        if (dt <= 0) return null
        return haversine(sm[i - 1], sm[i]) / dt
    })
    const speedKmh = smooth(rawSpeedMs, 3).map(v => v == null ? null : v * 3.6)

    // Cadence (steps/min) from the cumulative TYPE_STEP_COUNTER. The counter
    // updates in bursts and the logger occasionally emits sample PAIRS only
    // milliseconds apart — and on a batch flush slightly OUT OF ORDER, so the
    // count momentarily ticks DOWN. Dividing an adjacent-pair step delta by a
    // sub-second dt makes cadence explode (1 step / 0.01s = 6000 spm) or go
    // negative (a -2 reorder = a huge negative spike) — exactly the garbage
    // that smoothing the *result* can't remove (a 4-wide MA just smears a
    // +4600 spike into a ~700 bump). So measure the rate over a trailing time
    // WINDOW (count gained over the last ~CADENCE_WIN_MS), which is what
    // cadence means anyway: the dt is always ~window-sized so tiny-dt blowups
    // can't happen, and a transient -2 is dwarfed by the window's real gain.
    // Then clamp to a physiological ceiling and gently smooth what's left.
    const CADENCE_WIN_MS = 30000   // trailing window the rate is measured over
    const MAX_CADENCE_SPM = 200    // brisk walk ~120-140, run ~180; above this = sensor artifact
    const hasSteps = sorted.some(p => typeof p.steps === 'number' && p.steps != null)
    const rawCadenceSpm: (number | null)[] = sorted.map((p, i) => {
        if (!hasSteps || i === 0 || p.steps == null) return null
        // Walk back to the earliest sample still inside the trailing window.
        let j = i
        while (j > 0 && (p.ts - sorted[j - 1].ts) <= CADENCE_WIN_MS) j--
        const prev = sorted[j]
        if (j === i || prev.steps == null) return null   // no usable window start
        const ds = p.steps - prev.steps
        const dtMs = p.ts - prev.ts
        if (dtMs <= 0 || ds < 0) return null              // counter went backwards / zero span
        const spm = (ds / dtMs) * 60000
        return spm > MAX_CADENCE_SPM ? null : spm         // clamp out residual artifacts
    })
    const cadenceSpm = smooth(rawCadenceSpm, 4)

    // Altitude profile — prefer GPS `alt` (sparser but absolute), fall back
    // to barometric-derived altitude (smoother on devices with a sensor).
    const hasAlt = sorted.some(p => typeof p.alt === 'number' && p.alt != null) ||
                   sorted.some(p => typeof p.hpa === 'number' && p.hpa != null)
    const absAlt: (number | null)[] = sorted.map(p => {
        if (typeof p.alt === 'number' && p.alt != null) return p.alt
        if (typeof p.hpa === 'number' && p.hpa != null) return hpaToAltMeters(p.hpa)
        return null
    })
    const firstAlt = absAlt.find(v => v != null) ?? 0
    const altitudeM = absAlt.map(v => v == null ? null : v - (firstAlt as number))

    // Walk-relative step total — only meaningful if both endpoints have a step reading.
    let walkStepsTotal: number | null = null
    const firstStepIdx = sorted.findIndex(p => p.steps != null)
    const lastStepIdx = (() => { for (let i = sorted.length - 1; i >= 0; i--) if (sorted[i].steps != null) return i; return -1 })()
    if (firstStepIdx >= 0 && lastStepIdx > firstStepIdx) {
        walkStepsTotal = (sorted[lastStepIdx].steps as number) - (sorted[firstStepIdx].steps as number)
    }

    // Avg speed = smoothed distance / duration. Distance is the length of the
    // Kalman-smoothed track (smoothed.distanceMeters), which is what the detail
    // header shows and what the backend recomputes & stores, so the per-walk
    // number, the avg speed, and the day/week/month totals all agree.
    const totalMeters = smoothed.distanceMeters
    const totalSec = (sorted[sorted.length - 1].ts - t0) / 1000
    const avgSpeedKmh = totalSec > 0 ? (totalMeters / totalSec) * 3.6 : null

    return { minutes, speedKmh, cadenceSpm, altitudeM, hasSteps, hasAlt: hasAlt && absAlt.some(v => v != null), walkStepsTotal, avgSpeedKmh, distanceMeters: totalMeters, smoothedLatLng: sm }
}

interface DailyTotal {
    dateKey: string
    label: string
    /** Walking only — this is the headline "distance walked". */
    distanceKm: number
    /** The rest of the same sessions, so the chart can show context without
     *  inflating the walking figure. */
    vehicleKm: number
    otherKm: number
    walkCount: number
    durationMin: number
}

/**
 * Avg speed is only meaningful when the session was mostly walking. A session
 * that is 157 m of walking inside an 8 km drive divides walking distance by
 * driving time and reports 0.3 km/h — a number that describes nothing. We have
 * distance split by class but not duration, so the honest move is to withhold
 * the figure rather than compute a wrong one.
 *
 * The test is walking vs VEHICLE only — `other` is deliberately excluded.
 * `other` is partly "we could not tell": the classifier can only ever put a
 * window there when step data is untrustworthy, so counting it against walking
 * penalises exactly the short, slow, stop-start walks that produce patchy step
 * data. Measured across all 333 classified rows: 225 sessions qualify as walks,
 * 88 of them are not walking-dominant once `other` is included, and 16 of those
 * flip back to walking-dominant when it is excluded. Those 16 are real walks
 * that would otherwise silently lose their pace display.
 *
 * Vehicle distance is different — it is positive evidence of not-walking, so it
 * still disqualifies.
 */
function predominantlyWalking(w: WalkModel): boolean {
    const walk = w.walkingMeters
    if (typeof walk !== 'number') return true          // unclassified: legacy behaviour
    return walk > (w.vehicleMeters ?? 0)
}

/**
 * Names why sessions were excluded, for the all-excluded empty state.
 *
 * There are two independent reasons and the count covers both, so this must not
 * assume either one. It previously hardcoded "motion-triggered", which told a
 * caregiver the wrong thing on the common case: a day of driving with no
 * qualifying walk, where every session was in fact cadence-triggered.
 */
function excludedReasonPhrase(e: { total: number; motion: number; noWalking: number }): string {
    const plural = e.total !== 1
    if (e.motion > 0 && e.noWalking > 0) {
        return plural
            ? 'were either started by device motion or contained under 50 m of walking'
            : 'was either started by device motion or contained under 50 m of walking'
    }
    if (e.motion > 0) return plural ? 'were started by device motion, not walking' : 'was started by device motion, not walking'
    // Number-invariant on purpose — "contained" reads correctly for one or many.
    return 'contained under 50 m of walking'
}

/** Calendar day the walk happened on, on the walker's local clock. */
function walkDateKey(w: WalkModel, homeZone: string): string {
    return DateTime.fromMillis(w.startedAt, { zone: walkZone(w, homeZone) }).toFormat('yyyy-MM-dd')
}

/** Clock time in the zone where the walk happened. */
function walkClock(ms: number, w: WalkModel, homeZone: string, fmt: string): string {
    return DateTime.fromMillis(ms, { zone: walkZone(w, homeZone) }).toFormat(fmt)
}

/**
 * " GMT+2" (or " EDT", per the browser's locale) when the walk's zone offset
 * differs from the home's at that instant, so a travelling walker's times
 * aren't mistaken for home time. Empty when they agree.
 */
function walkZoneLabel(w: WalkModel, homeZone: string): string {
    const local = DateTime.fromMillis(w.startedAt, { zone: walkZone(w, homeZone) })
    const home = DateTime.fromMillis(w.startedAt, { zone: homeZone })
    return local.offset !== home.offset ? ' ' + local.toFormat('ZZZZ') : ''
}

function dailyTotals(walks: WalkModel[], timezone: string): DailyTotal[] {
    const buckets = new Map<string, DailyTotal>()
    for (const w of walks) {
        const key = walkDateKey(w, timezone)
        const ended = w.endedAt ?? Date.now()
        let g = buckets.get(key)
        if (!g) {
            g = { dateKey: key, label: DateTime.fromISO(key).toFormat('MMM d'), distanceKm: 0, vehicleKm: 0, otherKm: 0, walkCount: 0, durationMin: 0 }
            buckets.set(key, g)
        }
        g.distanceKm += walkingMetersOf(w) / 1000
        g.vehicleKm += (w.vehicleMeters ?? 0) / 1000
        g.otherKm += (w.otherMeters ?? 0) / 1000
        g.walkCount += 1
        g.durationMin += Math.max(0, ended - w.startedAt) / 60000
    }
    const out = Array.from(buckets.values())
    out.sort((a, b) => a.dateKey < b.dateKey ? -1 : 1)
    return out
}

function rangeForMode(mode: Mode, dateOffset: number, timezone: string): { from: number; to: number; dayCount: number } {
    const anchor = DateTime.now().setZone(timezone).startOf('day').plus({ days: dateOffset })
    const dayCount = mode === 'day' ? 1 : mode === 'week' ? 7 : mode === 'month' ? 30 : 180
    const to = anchor.endOf('day').toMillis()
    const from = anchor.minus({ days: dayCount - 1 }).startOf('day').toMillis()
    return { from, to, dayCount }
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Walks are grouped by the day they happened on the walker's local clock, which
 * can be a day either side of the home-zone day. Fetch a day wider each side,
 * then keep walks whose local date falls in the range. For "Now" (offset 0) there
 * is no upper bound: a walk abroad may already be on tomorrow's date in home time.
 */
function fetchRangeForMode(mode: Mode, dateOffset: number, timezone: string): { from: number; to: number } {
    const { from, to } = rangeForMode(mode, dateOffset, timezone)
    return { from: from - DAY_MS, to: dateOffset === 0 ? Date.now() + DAY_MS : to + DAY_MS }
}

function dateKeyRange(mode: Mode, dateOffset: number, timezone: string): { fromKey: string; toKey: string | null } {
    const { from, to } = rangeForMode(mode, dateOffset, timezone)
    return {
        fromKey: DateTime.fromMillis(from, { zone: timezone }).toFormat('yyyy-MM-dd'),
        toKey: dateOffset === 0 ? null : DateTime.fromMillis(to, { zone: timezone }).toFormat('yyyy-MM-dd'),
    }
}

function inKeyRange(key: string, r: { fromKey: string; toKey: string | null }): boolean {
    return key >= r.fromKey && (r.toKey == null || key <= r.toKey)
}

export function WalksReport(props: WalksReportProps) {
    const [mode, setMode] = useState<Mode>('day')
    const [walks, setWalks] = useState<WalkModel[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [selectedWalkId, setSelectedWalkId] = useState<string | null>(null)
    const [pointsByWalk, setPointsByWalk] = useState<Record<string, WalkPointModel[]>>({})
    const [loadingPoints, setLoadingPoints] = useState(false)
    const [aggregateMetric, setAggregateMetric] = useState<'distance' | 'walks' | 'duration'>('distance')
    // Auto-refresh polls the walks list and the currently-selected walk's
    // points every ~25s while a walk is in progress. Off by default — when
    // nobody's actively watching a live walk, it's pointless network noise.
    const [autoRefresh, setAutoRefresh] = useState(false)
    // When the walker's phone is in "any motion" trigger mode, some sessions
    // are drives rather than walks. Default to walking only — a car trip folded
    // into "distance walked today" is actively misleading to a caregiver. The
    // toggle only appears when there's motion data in range, so nothing about
    // this view changes for a phone in the default cadence mode.
    const [includeMotion, setIncludeMotion] = useState(false)

    const mapRef = useRef<HTMLDivElement | null>(null)
    const mapInstance = useRef<L.Map | null>(null)
    const layerRef = useRef<L.LayerGroup | null>(null)

    // Range fetched is wider than the active mode so switching Day↔Week↔Month
    // doesn't trigger a refetch every click. Always fetch the largest
    // requested range; aggregate views slice from the same array.
    useEffect(() => {
        let cancelled = false
        setError(null)
        setWalks(null)
        setSelectedWalkId(null)
        if (props.beneficiaryUserId == null) { setWalks([]); return }
        const { from, to } = fetchRangeForMode(mode, props.dateOffset, props.timezone)
        DataService.getWalks(props.beneficiaryUserId, from, to)
            .then(rsp => {
                if (cancelled) return
                rsp.sort((a, b) => b.startedAt - a.startedAt)
                setWalks(rsp)
            })
            .catch(e => { if (!cancelled) setError(e?.message || 'Could not load walks') })
        return () => { cancelled = true }
    }, [props.beneficiaryUserId, props.timezone, props.dateOffset, mode])

    // Sessions excluded from the walks view, split by reason so the banner can
    // say which. Zero on a cadence-mode phone whose sessions all contain real
    // walking — in which case no banner or toggle appears at all.
    // The fetch is a day wider each side; keep only walks whose local date is in range.
    const rangeWalks = useMemo(() => {
        if (!walks) return null
        const r = dateKeyRange(mode, props.dateOffset, props.timezone)
        return walks.filter(w => inKeyRange(walkDateKey(w, props.timezone), r))
    }, [walks, mode, props.dateOffset, props.timezone])

    const excluded = useMemo(() => {
        const list = (rangeWalks || []).filter(w => !countsAsWalk(w))
        return {
            total: list.length,
            motion: list.filter(w => !isWalkingSession(w)).length,
            noWalking: list.filter(w => isWalkingSession(w) && w.isWalk === false).length,
        }
    }, [rangeWalks])

    // Single filtered source for every downstream aggregation, so totals, the
    // day list and the daily chart can never disagree about what counts.
    const visibleWalks = useMemo(() => {
        if (!rangeWalks) return null
        return includeMotion ? rangeWalks : rangeWalks.filter(countsAsWalk)
    }, [rangeWalks, includeMotion])

    // Hiding the selected walk would leave the map and charts rendering a walk
    // that's no longer in the list.
    useEffect(() => {
        if (!selectedWalkId || !visibleWalks) return
        if (!visibleWalks.some(w => w.id === selectedWalkId)) setSelectedWalkId(null)
    }, [visibleWalks, selectedWalkId])

    const totalsForRange = useMemo(() => {
        if (!visibleWalks) return { distanceKm: 0, walkCount: 0, durationMin: 0 }
        let distanceKm = 0, durationMin = 0
        for (const w of visibleWalks) {
            distanceKm += walkingMetersOf(w) / 1000
            const end = w.endedAt ?? Date.now()
            durationMin += Math.max(0, end - w.startedAt) / 60000
        }
        return { distanceKm, walkCount: visibleWalks.length, durationMin }
    }, [visibleWalks])

    const dayWalks = useMemo(() => {
        if (!visibleWalks) return []
        const r = dateKeyRange('day', props.dateOffset, props.timezone)
        return visibleWalks.filter(w => inKeyRange(walkDateKey(w, props.timezone), r))
    }, [visibleWalks, props.dateOffset, props.timezone])

    const aggData = useMemo(() => dailyTotals(visibleWalks || [], props.timezone), [visibleWalks, props.timezone])

    const inProgress = visibleWalks?.find(w => w.endedAt == null)

    // Fetch the selected walk's points once the user opens it.
    useEffect(() => {
        if (!selectedWalkId) return
        if (pointsByWalk[selectedWalkId]) return
        if (props.beneficiaryUserId == null) return
        const beneficiaryUserId = props.beneficiaryUserId
        let cancelled = false
        setLoadingPoints(true)
        DataService.getWalkPoints(beneficiaryUserId, selectedWalkId)
            .then(pts => {
                if (cancelled) return
                setPointsByWalk(prev => ({ ...prev, [selectedWalkId]: pts }))
            })
            .catch(() => { /* leave undefined; map stays in loading shell */ })
            .finally(() => { if (!cancelled) setLoadingPoints(false) })
        return () => { cancelled = true }
    }, [selectedWalkId, props.beneficiaryUserId])

    const selectedWalk = selectedWalkId ? (walks?.find(w => w.id === selectedWalkId) || null) : null
    const selectedPoints = selectedWalkId ? pointsByWalk[selectedWalkId] || null : null
    const selectedSeries: WalkSeries | null = useMemo(() => selectedPoints ? computeWalkSeries(selectedPoints) : null, [selectedPoints])

    // Fixed order so the legend never reshuffles as a track's mix changes.
    const selectedSegmentClasses = useMemo<WalkPointClass[]>(() => {
        if (!selectedPoints || selectedPoints.length < 2) return []
        const seen = new Set<WalkPointClass>()
        for (let i = 1; i < selectedPoints.length; i++) seen.add(segmentClass(selectedPoints[i - 1], selectedPoints[i]))
        return (['walking', 'vehicle', 'other'] as WalkPointClass[]).filter(c => seen.has(c))
    }, [selectedPoints])


    // Average accuracy reported across the selected walk's points. Surfaced
    // in the detail header so the user can see why a polyline may look
    // zig-zaggy: urban / indoor GPS routinely returns 30-50m accuracy, and
    // straight lines between samples at that radius can fold back on
    // themselves. PWA doesn't filter low-accuracy points (that'd hide data);
    // we just label the quality.
    const selectedAvgAccuracy: number | null = useMemo(() => {
        if (!selectedPoints || !selectedPoints.length) return null
        const accs = selectedPoints.map(p => p.acc).filter((a): a is number => typeof a === 'number' && isFinite(a))
        if (!accs.length) return null
        return accs.reduce((a, b) => a + b, 0) / accs.length
    }, [selectedPoints])

    // Latest-point timestamp for the selected walk — surfaced so we can tell
    // the difference between "walk is genuinely in progress" (recent points
    // arriving) and "walk's status row says null endedAt but the mobile
    // hasn't uploaded in 20 min" (stale; auto-stop didn't fire or the upload
    // queue is jammed). Backend has no cleanup sweep, so the latter looks
    // identical to the former unless the client checks the data freshness.
    const selectedLastPointTs: number | null = useMemo(() => {
        if (!selectedPoints || !selectedPoints.length) return null
        return Math.max(...selectedPoints.map(p => p.ts))
    }, [selectedPoints])
    const STALE_THRESHOLD_MS = 5 * 60 * 1000
    // useNow ticks every 30s so the staleness label updates as we wait —
    // otherwise "1 min ago" would stay frozen until something else re-renders.
    const [, setNowTick] = useState(0)
    useEffect(() => {
        const t = setInterval(() => setNowTick(n => n + 1), 30000)
        return () => clearInterval(t)
    }, [])

    function formatAge(ms: number): string {
        const sec = Math.max(0, Math.floor(ms / 1000))
        if (sec < 90) return `${sec}s ago`
        const min = Math.round(sec / 60)
        if (min < 60) return `${min} min ago`
        const h = Math.floor(min / 60), m = min % 60
        return m ? `${h}h ${m}m ago` : `${h}h ago`
    }

    // Auto-refresh loop. Polls the walks list (so distance/duration of any
    // in-progress walk grows), and if a walk is open, its points too. 25s
    // is a balance between "looks live" and "doesn't hammer the backend";
    // the underlying GPS samples at ~10s anyway, so faster polling just
    // re-fetches the same data.
    useEffect(() => {
        if (!autoRefresh) return
        if (props.beneficiaryUserId == null) return
        const beneficiaryUserId = props.beneficiaryUserId
        let cancelled = false
        const tick = () => {
            const { from, to } = fetchRangeForMode(mode, props.dateOffset, props.timezone)
            DataService.getWalks(beneficiaryUserId, from, to)
                .then(rsp => {
                    if (cancelled) return
                    rsp.sort((a, b) => b.startedAt - a.startedAt)
                    setWalks(rsp)
                })
                .catch(() => { /* swallow — keep the loop alive */ })
            if (selectedWalkId) {
                DataService.getWalkPoints(beneficiaryUserId, selectedWalkId)
                    .then(pts => {
                        if (cancelled) return
                        setPointsByWalk(prev => ({ ...prev, [selectedWalkId]: pts }))
                    })
                    .catch(() => {})
            }
        }
        const interval = setInterval(tick, 25000)
        return () => { cancelled = true; clearInterval(interval) }
    }, [autoRefresh, props.beneficiaryUserId, props.timezone, props.dateOffset, mode, selectedWalkId])

    // Render the selected walk's polyline + start/end markers on the inline map.
    useEffect(() => {
        if (!selectedWalkId || !selectedPoints || !mapRef.current) return

        if (mapInstance.current && mapInstance.current.getContainer() !== mapRef.current) {
            mapInstance.current.remove()
            mapInstance.current = null
            layerRef.current = null
        }
        if (!mapInstance.current) {
            mapInstance.current = L.map(mapRef.current, { zoomControl: true })
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '© OpenStreetMap contributors'
            }).addTo(mapInstance.current)
            layerRef.current = L.layerGroup().addTo(mapInstance.current)
        }
        const map = mapInstance.current
        const layer = layerRef.current!
        layer.clearLayers()

        // Draw the Kalman-smoothed track, not the raw fixes. The smoothed
        // positions align 1:1 with the ts-sorted points, so per-segment
        // accuracy dimming (read from the raw points below) still lines up.
        const smoothedLatLng = selectedSeries?.smoothedLatLng
        const latlngs = (smoothedLatLng && smoothedLatLng.length === selectedPoints.length)
            ? smoothedLatLng.map(p => [p.lat, p.lng] as [number, number])
            : selectedPoints.map(p => [p.lat, p.lng] as [number, number])
        if (latlngs.length) {
            // PWA_HANDOFF_walks_v2.md §2.3 Option A — per-segment polylines
            // dimmed by GPS accuracy. Caregivers see the whole path AND get
            // a visual cue about where to trust it: solid blue where the fix
            // was tight (~10m), faded where urban multipath blew it out
            // (40-50m+). Mobile deliberately doesn't drop low-accuracy
            // points; visualisation honesty lives here.
            for (let i = 1; i < selectedPoints.length; i++) {
                const prev = selectedPoints[i - 1]
                const cur = selectedPoints[i]
                const accs: number[] = []
                if (typeof prev.acc === 'number' && isFinite(prev.acc)) accs.push(prev.acc)
                if (typeof cur.acc === 'number' && isFinite(cur.acc)) accs.push(cur.acc)
                const avgAcc = accs.length ? accs.reduce((a, b) => a + b, 0) / accs.length : null
                // Map acc → opacity: 10m=0.85 (full), 50m=0.2 (very faded).
                // Null accuracy gets the default 0.85.
                const opacity = avgAcc == null ? 0.85 : Math.max(0.2, Math.min(0.85, 1 - (avgAcc - 10) / 40))
                // Layered on top of the accuracy fade: how the segment was
                // travelled. Walking keeps the solid primary stroke; vehicle and
                // other are dashed and muted, so a 285 m walk inside a 15 km
                // drive reads as a walk with context rather than a highway trip.
                // Dash pattern doubles for colour so the classes stay separable.
                const style = SEGMENT_STYLE[segmentClass(prev, cur)]
                L.polyline([latlngs[i - 1], latlngs[i]], {
                    color: style.color,
                    weight: style.weight,
                    dashArray: style.dashArray,
                    opacity,
                }).addTo(layer)
            }
            L.circleMarker(latlngs[0], { radius: 7, color: '#fff', fillColor: '#22c55e', fillOpacity: 1, weight: 2 }).addTo(layer)
            const last = latlngs[latlngs.length - 1]
            if (selectedWalk?.endedAt == null) {
                L.circleMarker(last, { radius: 9, color: '#fff', fillColor: '#2563eb', fillOpacity: 0.9, weight: 2 }).addTo(layer)
            } else {
                L.circleMarker(last, { radius: 7, color: '#fff', fillColor: '#ef4444', fillOpacity: 1, weight: 2 }).addTo(layer)
            }
            map.fitBounds(L.latLngBounds(latlngs).pad(0.15))
        }
        setTimeout(() => map.invalidateSize(), 50)
    }, [selectedWalkId, selectedPoints, selectedSeries, selectedWalk?.endedAt])

    useEffect(() => {
        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove()
                mapInstance.current = null
                layerRef.current = null
            }
        }
    }, [])

    const lineConfig: ChartConfig = { value: { label: '', color: '#0066cc' } }

    // Recharts wants flat data points: pre-zip the parallel arrays.
    const speedData = selectedSeries
        ? selectedSeries.minutes.map((t, i) => ({ t, value: selectedSeries.speedKmh[i] })).filter(d => d.value != null)
        : []
    const cadenceData = selectedSeries
        ? selectedSeries.minutes.map((t, i) => ({ t, value: selectedSeries.cadenceSpm[i] })).filter(d => d.value != null)
        : []
    const altitudeData = selectedSeries
        ? selectedSeries.minutes.map((t, i) => ({ t, value: selectedSeries.altitudeM[i] })).filter(d => d.value != null)
        : []

    function metricButtonCls(active: boolean) {
        return active
            ? 'px-3 py-1 text-xs font-medium text-white bg-blue-500 rounded-lg'
            : 'px-3 py-1 text-xs font-medium bg-gray-200 rounded-lg hover:bg-gray-300'
    }

    function modeButtonCls(active: boolean) {
        return active
            ? 'px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg'
            : 'px-4 py-2 text-sm font-medium bg-gray-200 rounded-lg hover:bg-gray-300'
    }

    return (
        <Card className="p-4 space-y-4 shadow-md rounded-lg border" id="walks_report">
            <div className="flex justify-between items-center border-b pb-2">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <Footprints className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Walks
                </h3>
                <DatePicker value={props.dateOffset} onChange={props.onDateChange} timezone={props.timezone} />
            </div>

            <div className="flex justify-center gap-2">
                <button className={modeButtonCls(mode === 'day')} onClick={() => setMode('day')}>Day</button>
                <button className={modeButtonCls(mode === 'week')} onClick={() => setMode('week')}>Week</button>
                <button className={modeButtonCls(mode === 'month')} onClick={() => setMode('month')}>1Mo</button>
                <button className={modeButtonCls(mode === '6mo')} onClick={() => setMode('6mo')}>6Mo</button>
            </div>

            <div className="flex justify-between items-center text-xs">
                <div>
                    {inProgress && (
                        <span className="inline-flex items-center gap-1 font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded-full">
                            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" /> Walk in progress
                        </span>
                    )}
                </div>
                <label className="inline-flex items-center gap-2 text-gray-600 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        className="w-4 h-4 accent-blue-600"
                        checked={autoRefresh}
                        onChange={e => setAutoRefresh(e.target.checked)}
                    />
                    Auto-refresh
                </label>
            </div>

            {excluded.total > 0 && (
                <div className="flex items-center justify-between gap-3 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <span className="text-amber-900">
                        {excluded.total} {excluded.total === 1 ? 'session' : 'sessions'} in this range
                        {excluded.total === 1 ? ' is' : ' are'} not walks
                        {includeMotion ? ' — shown below anyway.' : ' — hidden, and not counted in the totals.'}
                        <span className="block text-amber-700/80">
                            {[
                                excluded.noWalking > 0 && `${excluded.noWalking} contained under 50 m of walking (mostly driving or stationary)`,
                                excluded.motion > 0 && `${excluded.motion} started from device motion rather than walking cadence`,
                            ].filter(Boolean).join('; ')}.
                        </span>
                    </span>
                    <label className="inline-flex shrink-0 items-center gap-2 text-amber-900 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            className="w-4 h-4 accent-amber-600"
                            checked={includeMotion}
                            onChange={e => setIncludeMotion(e.target.checked)}
                        />
                        Show
                    </label>
                </div>
            )}

            {error ? (
                <div className="text-center text-red-500 text-sm">Error: {error}</div>
            ) : walks === null ? (
                <div className="text-center text-gray-600 text-sm flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin" size={16} /> Loading walks…
                </div>
            ) : (visibleWalks?.length ?? 0) === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
                    {excluded.total > 0 && !includeMotion
                        ? `No walks recorded in this ${mode === 'day' ? 'day' : 'period'} — the ${excluded.total === 1 ? 'one session' : `${excluded.total} sessions`} here ${excludedReasonPhrase(excluded)}, so ${excluded.total === 1 ? 'it is' : 'they are'} hidden. Tick “Show” above to see ${excluded.total === 1 ? 'it' : 'them'}.`
                        : `No walks recorded in this ${mode === 'day' ? 'day' : 'period'}. Walks appear here once they're recorded on the loved one's phone and walk tracking is enabled.`}
                </div>
            ) : mode === 'day' ? (
                <DayView
                    walks={dayWalks}
                    timezone={props.timezone}
                    selectedWalkId={selectedWalkId}
                    onSelect={setSelectedWalkId}
                />
            ) : (
                <AggregateView
                    data={aggData}
                    totals={totalsForRange}
                    metric={aggregateMetric}
                    setMetric={setAggregateMetric}
                    metricButtonCls={metricButtonCls}
                />
            )}

            {selectedWalk && (() => {
                const lastPointAgeMs = selectedLastPointTs ? Date.now() - selectedLastPointTs : null
                const isStale = selectedWalk.endedAt == null && lastPointAgeMs != null && lastPointAgeMs > STALE_THRESHOLD_MS
                return (
                <div className="border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-2 bg-gray-50 border-b">
                        <div>
                            <p className="text-sm font-medium text-gray-800 inline-flex items-center gap-2">
                                {walkClock(selectedWalk.startedAt, selectedWalk, props.timezone, 'cccc, LLL d · HH:mm')}
                                {selectedWalk.endedAt && ' – ' + walkClock(selectedWalk.endedAt, selectedWalk, props.timezone, 'HH:mm')}
                                {walkZoneLabel(selectedWalk, props.timezone)}
                                {selectedWalk.endedAt == null && (
                                    isStale ? (
                                        <span className="text-[10px] uppercase tracking-wide text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded" title="Walk row says in-progress but no new points have arrived. The walker's phone likely paused or stopped uploading without finalising the walk.">stale</span>
                                    ) : (
                                        <span className="text-[10px] uppercase tracking-wide text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">live</span>
                                    )
                                )}
                            </p>
                            <p className="text-xs text-gray-500">
                                {/* The list row shows walked distance; this panel describes the whole
                                    track the map is drawing. Showing only one of them made the same
                                    session read as 163 m in the list and 4.79 km here. Name both. */}
                                {formatDistance(walkingMetersOf(selectedWalk))} walked
                                {typeof selectedWalk.walkingMeters === 'number'
                                    && Math.abs((selectedSeries?.distanceMeters ?? selectedWalk.distanceMeters) - selectedWalk.walkingMeters) > 50
                                    && ' · ' + formatDistance(selectedSeries?.distanceMeters ?? selectedWalk.distanceMeters) + ' total track'}
                                {' · ' + formatDuration((selectedWalk.endedAt ?? Date.now()) - selectedWalk.startedAt)}
                                {selectedSeries?.avgSpeedKmh != null && predominantlyWalking(selectedWalk) && ' · ' + selectedSeries.avgSpeedKmh.toFixed(1) + ' km/h avg'}
                                {selectedSeries?.walkStepsTotal != null && ' · ' + selectedSeries.walkStepsTotal.toLocaleString() + ' steps'}
                                {selectedAvgAccuracy != null && ' · ±' + Math.round(selectedAvgAccuracy) + 'm gps'}
                                {lastPointAgeMs != null && ' · last sample ' + formatAge(lastPointAgeMs)}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {loadingPoints && <Loader2 className="animate-spin text-gray-500" size={14} />}
                            <button className="text-xs text-gray-600 hover:underline px-2" onClick={() => setSelectedWalkId(null)}>Close</button>
                        </div>
                    </div>
                    <div ref={mapRef} style={{ height: 320, width: '100%' }} />
                    {/* Legend appears only when the track holds more than one class —
                        an ordinary walk stays uncluttered. Identity is never colour
                        alone: each entry pairs its stroke with a written label. */}
                    {selectedSegmentClasses.length > 1 && (
                        <ul className="flex flex-wrap gap-4 px-3 pt-2 text-xs text-gray-600">
                            {selectedSegmentClasses.map(c => (
                                <li key={c} className="flex items-center gap-2">
                                    <svg width="22" height="8" aria-hidden="true">
                                        <line
                                            x1="1" y1="4" x2="21" y2="4"
                                            stroke={SEGMENT_STYLE[c].color}
                                            strokeWidth={SEGMENT_STYLE[c].weight}
                                            strokeDasharray={SEGMENT_STYLE[c].dashArray}
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    {SEGMENT_STYLE[c].label}
                                </li>
                            ))}
                        </ul>
                    )}

                    {selectedSeries && (
                        <div className="p-3 space-y-4">
                            <PerWalkChart
                                title="Speed"
                                icon={<Gauge size={14} />}
                                data={speedData}
                                unit="km/h"
                                color="#0066cc"
                                config={lineConfig}
                            />
                            {selectedSeries.hasSteps && (
                                <PerWalkChart
                                    title="Cadence"
                                    icon={<Footprints size={14} />}
                                    data={cadenceData}
                                    unit="steps/min"
                                    color="#16a34a"
                                    config={lineConfig}
                                />
                            )}
                            {selectedSeries.hasAlt && (
                                <PerWalkChart
                                    title="Altitude (relative)"
                                    icon={<Mountain size={14} />}
                                    data={altitudeData}
                                    unit="m"
                                    color="#a855f7"
                                    config={lineConfig}
                                />
                            )}
                            {!selectedSeries.hasSteps && !selectedSeries.hasAlt && (
                                <p className="text-xs text-gray-500 text-center">
                                    {!isWalkingSession(selectedWalk)
                                        ? 'No cadence for this session — it was started by device motion rather than walking, so there are no steps to measure.'
                                        : 'No cadence or altitude data for this walk. Older walks recorded before the sensor upgrade will only have speed.'}
                                </p>
                            )}
                        </div>
                    )}
                    {isStale && (
                        <div className="p-2 bg-amber-50 border-t text-xs text-amber-800">
                            No new GPS samples in {lastPointAgeMs != null && formatAge(lastPointAgeMs)}. The walker's phone may have lost network or stopped tracking without finalising the walk.
                        </div>
                    )}
                </div>
                )
            })()}
        </Card>
    )
}

function DayView({ walks, timezone, selectedWalkId, onSelect }: {
    walks: WalkModel[]
    timezone: string
    selectedWalkId: string | null
    onSelect: (id: string | null) => void
}) {
    if (!walks.length) {
        return <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">No walks recorded on this day.</div>
    }

    let totalMeters = 0, totalMs = 0
    // Speed is accumulated only over walking-dominant sessions; mixing a drive's
    // duration into a walking-distance numerator produces a meaningless rate.
    let speedMeters = 0, speedMs = 0
    for (const w of walks) {
        const m = walkingMetersOf(w)
        const ms = Math.max(0, (w.endedAt ?? Date.now()) - w.startedAt)
        totalMeters += m
        totalMs += ms
        if (predominantlyWalking(w)) { speedMeters += m; speedMs += ms }
    }
    const avgSpeedMs = speedMs > 0 ? speedMeters / (speedMs / 1000) : 0

    return (
        <>
            <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-100 rounded-lg p-2">
                    <p className="text-sm font-semibold text-gray-800">{formatDistance(totalMeters)}</p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Distance</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-2">
                    <p className="text-sm font-semibold text-gray-800">{formatDuration(totalMs)}</p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Duration</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-2">
                    <p className="text-sm font-semibold text-gray-800">{formatSpeed(avgSpeedMs)}</p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Avg speed</p>
                </div>
            </div>

            <ul className="divide-y rounded-lg border">
                {walks.map(w => {
                    const start = walkClock(w.startedAt, w, timezone, 'HH:mm')
                    const end = w.endedAt ? walkClock(w.endedAt, w, timezone, 'HH:mm') : null
                    const durMs = (w.endedAt ?? Date.now()) - w.startedAt
                    const isSelected = w.id === selectedWalkId
                    const avgSpeed = durMs > 0 && predominantlyWalking(w) ? walkingMetersOf(w) / (durMs / 1000) : 0
                    return (
                        <li
                            key={w.id}
                            className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                            onClick={() => onSelect(isSelected ? null : w.id)}
                        >
                            <div>
                                <p className="text-sm font-medium text-gray-800">
                                    {start}{end ? ` – ${end}` : ''}{walkZoneLabel(w, timezone)}
                                    {w.endedAt == null && (
                                        <span className="ml-2 text-[10px] uppercase tracking-wide text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">live</span>
                                    )}
                                    {w.isWalk === false && isWalkingSession(w) && (
                                        <span
                                            className="ml-2 text-[10px] uppercase tracking-wide text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded"
                                            title="Under 50 m of actual walking in this session"
                                        >no walking</span>
                                    )}
                                    {!isWalkingSession(w) && (
                                        <span
                                            className="ml-2 text-[10px] uppercase tracking-wide text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded"
                                            title="Started by device motion, not walking cadence — may not be a walk"
                                        >motion</span>
                                    )}
                                </p>
                                <p className="text-xs text-gray-500">{formatDistance(walkingMetersOf(w))} · {formatDuration(durMs)} · {formatSpeed(avgSpeed)}</p>
                            </div>
                            {isSelected
                                ? <ChevronDown size={16} className="text-gray-400" />
                                : <ChevronRight size={16} className="text-gray-400" />}
                        </li>
                    )
                })}
            </ul>
        </>
    )
}

function AggregateView({ data, totals, metric, setMetric, metricButtonCls }: {
    data: DailyTotal[]
    totals: { distanceKm: number; walkCount: number; durationMin: number }
    metric: 'distance' | 'walks' | 'duration'
    setMetric: (m: 'distance' | 'walks' | 'duration') => void
    metricButtonCls: (active: boolean) => string
}) {
    const config: ChartConfig = {
        // Walking keeps the primary hue; vehicle and other are deliberately
        // muted greys — they are context, not peer series, and they match the
        // map's segment styling so the two views tell the same story.
        distance: { label: 'Walking (km)', color: '#2a78d6' },
        notWalking: { label: 'Not walking (km)', color: '#eb6834' },
        walks: { label: 'Walks', color: '#16a34a' },
        duration: { label: 'Duration (min)', color: '#a855f7' },
    }
    const activeKey = metric
    const yFormatter = (v: number) =>
        metric === 'distance' ? `${v.toFixed(1)}` :
        metric === 'walks' ? `${Math.round(v)}` :
        `${Math.round(v)}`
    const valueFor = (d: DailyTotal) =>
        metric === 'distance' ? d.distanceKm :
        metric === 'walks' ? d.walkCount :
        d.durationMin
    const chartData = data.map(d => ({
        label: d.label,
        value: valueFor(d),
        notWalking: d.vehicleKm + d.otherKm,
    }))
    // Stack the non-walking distance only when there is some. A run of ordinary
    // walks keeps the single-series bar it has always had, and no legend.
    const stacked = metric === 'distance' && data.some(d => d.vehicleKm > 0.005 || d.otherKm > 0.005)
    const stackSeries: { key: string; colorKey: string; label: string }[] = [
        { key: 'value', colorKey: 'distance', label: 'Walking' },
        { key: 'notWalking', colorKey: 'notWalking', label: 'Not walking' },
    ]

    return (
        <>
            <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-100 rounded-lg p-2">
                    <p className="text-sm font-semibold text-gray-800">{totals.distanceKm.toFixed(2)} km</p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Total distance</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-2">
                    <p className="text-sm font-semibold text-gray-800">{totals.walkCount}</p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Walks</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-2">
                    <p className="text-sm font-semibold text-gray-800">{formatDuration(totals.durationMin * 60000)}</p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Total duration</p>
                </div>
            </div>

            <div className="flex justify-center gap-2 flex-wrap">
                <button className={metricButtonCls(metric === 'distance')} onClick={() => setMetric('distance')}>
                    <span className="inline-flex items-center gap-1"><MapPinned size={12} /> Distance</span>
                </button>
                <button className={metricButtonCls(metric === 'walks')} onClick={() => setMetric('walks')}>
                    <span className="inline-flex items-center gap-1"><Footprints size={12} /> Walks</span>
                </button>
                <button className={metricButtonCls(metric === 'duration')} onClick={() => setMetric('duration')}>
                    <span className="inline-flex items-center gap-1"><ChartArea size={12} /> Duration</span>
                </button>
            </div>

            <CardContent className="px-0">
                <ChartContainer config={config} className="h-48 w-full">
                    <BarChart accessibilityLayer data={chartData}>
                        <CartesianGrid vertical={false} />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={6} />
                        <YAxis tickLine={false} axisLine={false} tickMargin={6} tickFormatter={yFormatter} />
                        <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
                        {stacked ? (
                            <>
                                {/* 2px surface gap between stacked segments keeps them
                                    readable where a thin band meets a thick one. */}
                                <Bar dataKey="value" stackId="d" fill="var(--color-distance)" stroke="#fff" strokeWidth={2} />
                                <Bar dataKey="notWalking" stackId="d" fill="var(--color-notWalking)" stroke="#fff" strokeWidth={2} radius={4} />
                            </>
                        ) : (
                            <Bar dataKey="value" fill={`var(--color-${activeKey})`} radius={4} />
                        )}
                    </BarChart>
                </ChartContainer>
                {stacked && (
                    <ul className="flex flex-wrap justify-center gap-4 pt-2 text-xs text-gray-600">
                        {stackSeries.map(sv => (
                            <li key={sv.key} className="flex items-center gap-1.5">
                                <span
                                    className="inline-block w-2.5 h-2.5 rounded-sm"
                                    style={{ background: `var(--color-${sv.colorKey})` }}
                                />
                                {sv.label}
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </>
    )
}

function PerWalkChart({ title, icon, data, unit, color, config }: {
    title: string
    icon: React.ReactNode
    data: { t: number; value: number | null }[]
    unit: string
    color: string
    config: ChartConfig
}) {
    if (!data.length) return null
    const mmFormat = (v: number) => `${Math.round(v)}m`
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium text-gray-700 inline-flex items-center gap-1">{icon} {title}</p>
                <p className="text-[10px] text-gray-500">{unit}</p>
            </div>
            <ChartContainer config={config} className="h-32 w-full">
                <LineChart accessibilityLayer data={data}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="t" tickLine={false} axisLine={false} tickMargin={4} tickFormatter={mmFormat} />
                    <YAxis tickLine={false} axisLine={false} tickMargin={4} width={32} />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
                    <Line dataKey="value" type="monotone" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
            </ChartContainer>
        </div>
    )
}
